import {calculatePagesAmount} from '../common';
import {transformDateToTime} from '../date';
import {format} from 'date-fns';

export const getCashbackOverviewResponseTransformer = (
  {data, pagination},
  time = false
) => ({
  overview: data.map(row => ({
    id: row.id,
    total: row.cashback_info?.total_transaction_amount || 0,
    cashback: row.cashback_info?.amount || 0,
    card: row.card_number,
    status: row.status,
    transaction_id: row.transaction_id,
    date: time ? transformDateToTime(row.created_at) : row.created_at,
    merchant: {
      id: row.merchant.id,
      name: row.merchant.name,
      photo: row.merchant.logo
    },
    discount: {
      type: row?.cashback_info?.discount_type,
      percentage: row?.cashback_info?.discount_percentage
    }
  })),
  pagination: {
    totalItemsAmount: pagination.total,
    totalPagesAmount: calculatePagesAmount({
      totalItems: pagination.total,
      itemsPerPage: pagination.limit
    }),
    currentPage: pagination.page,
    limit: pagination.limit,
    hasMore: pagination.has_more
  }
});

export const getCashbackWithdrawalHistoryResponseTransformer = ({
  data,
  pagination
}) => ({
  withdrawal: data.map(row => ({
    id: row.id,
    cashback: row.amount || 0,
    fee: row?.fee,
    card: row?.card_number,
    status: row?.status,
    date: row.created_at,
    merchant: {
      id: row?.merchant?.id,
      name: row?.merchant?.name,
      photo: row?.merchant?.logo
    },
    total: row.total,
    discount: {
      type: row?.discount?.type,
      percentage: row?.discount?.percentage
    }
  })),
  pagination: {
    totalItemsAmount: pagination.total,
    totalPagesAmount: calculatePagesAmount({
      totalItems: pagination.total,
      itemsPerPage: pagination.limit
    }),
    currentPage: pagination.page,
    limit: pagination.limit,
    hasMore: pagination.has_more
  }
});

export const getInvoiceHistoryResponseTransformer = ({data, pagination}) => ({
  invoices: data.map(row => ({
    id: row.id,
    amount: row.amount || 0,
    issuedDate: row?.created_at,
    date: row?.due_date || row?.updated_at || row?.dueDate,
    status: row?.status,
    invoiceId: row?.reference
  })),
  pagination: {
    totalItemsAmount: pagination.total,
    totalPagesAmount: calculatePagesAmount({
      totalItems: pagination.total,
      itemsPerPage: pagination.limit
    }),
    currentPage: pagination.page,
    limit: pagination.limit,
    hasMore: pagination.has_more
  }
});

export const getSimplePerformanceResponseTransformer = ({data}) => ({
  performance: data.map(row => ({
    key: row.name,
    value: row.total_cashback || 0
  }))
});

export const getSimplePerformanceByCategoryResponseTransformer = ({data}) => ({
  performanceByCategory: data.map(row => ({
    key: row.category_name,
    value: row.total_cashback || 0
  }))
});

export const getPerformanceResponseTransformer = ({data}, locale) => {
  const monthCounts = data.reduce((acc, row) => {
    const date = new Date(row.period_start);
    const monthName = format(date, 'MMM', {locale});

    acc[monthName] = (acc[monthName] || 0) + 1;
    return acc;
  }, {});

  return {
    performance: data.map(row => {
      const date = new Date(row.period_start);
      const monthName = format(date, 'MMM', {locale});

      const key =
        monthCounts[monthName] > 1
          ? format(date, 'MMM yyyy', {locale})
          : monthName;

      return {
        key: row.name || key,
        value: row.total_cashback || 0
      };
    })
  };
};

export const getBenefitUsageRateResponseTransformer = ({data}, locale) => {
  const monthCounts = data.reduce((acc, row) => {
    const date = new Date(row.month_year);
    const monthName = format(date, 'MMM', {locale});

    acc[monthName] = (acc[monthName] || 0) + 1;
    return acc;
  }, {});

  return {
    benefitUsageRate: data.map(row => {
      const date = new Date(row.month_year);
      const monthName = format(date, 'MMM', {locale});

      const key =
        monthCounts[monthName] > 1
          ? format(date, 'MMM yyyy', {locale})
          : monthName;

      return {
        key: row.category_name || key,
        value: row.usage_rate,
        benefitViews: row.benefit_views
      };
    })
  };
};

export const getTotalRedemptionResponseTransformer = ({data}) => ({
  totalRedemption: data.map(row => ({
    key: row.period_start,
    value: row.total_redemptions
  }))
});

export const getFundsFlowResponseTransformer = ({data, pagination}) => ({
  fundsFlow: data.map((row, index) => ({
    id: index,
    earned: row.total_cashback || 0,
    date: row.day_date
  })),
  pagination: {
    totalItemsAmount: pagination.total,
    totalPagesAmount: calculatePagesAmount({
      totalItems: pagination.total,
      itemsPerPage: pagination.limit
    }),
    currentPage: pagination.page,
    limit: pagination.limit,
    hasMore: pagination.has_more
  }
});

export const getFundsFlowOverviewResponseTransformer = ({
  data,
  pagination
}) => ({
  overview: data.map(row => ({
    id: row.id,
    cashback: row.amount || 0,
    date: transformDateToTime(row.created_at)
  })),
  pagination: {
    totalItemsAmount: pagination.total,
    totalPagesAmount: calculatePagesAmount({
      totalItems: pagination.total,
      itemsPerPage: pagination.limit
    }),
    currentPage: pagination.page,
    limit: pagination.limit,
    hasMore: pagination.has_more
  }
});

export const getInvoiceResponseTransformer = ({data}) => ({
  invoice: {
    id: data?.id,
    invoiceId: data?.payment_details?.reference,
    issuedDate: data?.created_at,
    date: data?.due_date || data?.updated_at || data?.dueDate,
    status: data?.status,
    total: data?.invoice_breakdown?.total_amount || 0,
    billFrom: {
      name: data?.bill_from?.name,
      address: {
        street: data?.bill_from?.street,
        apartment: data?.bill_from?.apartment,
        zip: data?.bill_from?.zip,
        city: data?.bill_from?.city,
        country: data?.bill_from?.country
      },
      email: data?.bill_from?.email
    },
    billTo: {
      name: data?.bill_to?.name,
      address: {
        street: data?.bill_to?.street,
        apartment: data?.bill_to?.apartment,
        zip: data?.bill_to?.zip,
        city: data?.bill_to?.city,
        country: data?.bill_to?.country
      },
      email: data?.bill_to?.email
    },
    commission: data?.invoice_breakdown?.platform_share,
    fee: data?.invoice_breakdown?.monthly_fee,
    organisationCommission: data?.invoice_breakdown?.organisations_share,
    billingPeriod: {
      startDate: data?.billing_period_start,
      endDate: data?.billing_period_end
    },
    transferDetails: {
      holder: data?.payment_details?.account_holder,
      iban: data?.payment_details?.['IBAN'],
      swift: data?.payment_details?.['SWIFT'],
      reference: data?.payment_details?.reference
    }
  }
});

export const getBankAccountResponseTransformer = data => {
  return data?.map(({account_number, ...otherData}) => ({
    iban: account_number,
    ...otherData
  }));
};
